import {
  ACCENT_COLORS,
  GREENC_COLORS,
  ORANGEC_COLORS,
  PINKC_COLORS,
  PRIMARY_COLORS,
  REDC_COLORS,
  S800_COLORS,
  SECONDARY_COLORS,
  VIOLETC_COLORS,
  YELLOWC_COLORS,
} from "@monthly/theme";
import { useTheme } from "@mui/material";
import HighchartsReact from "highcharts-react-official";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import { addWatermarkToHighchartsExport } from "../chartWatermark";
import type { ColorPalette } from "../column-chart-gradient/ColumnChartGradient";
import Highcharts from "../HighChartsConfig";
import {
  formatNumberWithSeparators,
  truncateNumber,
} from "../utils/formatting";

export type ColorName =
  | "primary"
  | "secondary"
  | "accent"
  | "yellowc"
  | "redc"
  | "greenc"
  | "violetc"
  | "orangec"
  | "pinkc"
  | "s800";

const COLOR_PALETTES: Record<ColorName, ColorPalette> = {
  primary: PRIMARY_COLORS,
  secondary: SECONDARY_COLORS,
  accent: ACCENT_COLORS,
  yellowc: YELLOWC_COLORS,
  redc: REDC_COLORS,
  greenc: GREENC_COLORS,
  violetc: VIOLETC_COLORS,
  orangec: ORANGEC_COLORS,
  pinkc: PINKC_COLORS,
  s800: S800_COLORS,
};

export interface ColumnChartByPointProps {
  categories: string[];
  series: {
    name: string;
    data: number[];
  }[];
  baseColor?: ColorName;
  height?: number | string;
  stacked?: boolean;
  showLegend?: boolean;
  showStackedLabels?: boolean;
  showDataLabels?: boolean;
  isPercentage?: boolean;
  decimalPlaces?: number;
  customStackTotals?: number[];
  units?: string;
}

export interface ColumnChartByPointRef {
  downloadImage: (filename?: string) => void;
}

/**
 * ColumnChartByPoint
 * - Si hay una sola serie, cada columna recibe un tono diferente de la paleta (de oscuro a claro)
 * - Si hay varias series, cada serie recibe un color distinto (como ColumnChartGradient)
 */
export const ColumnChartByPoint = forwardRef<
  ColumnChartByPointRef,
  ColumnChartByPointProps
>(
  (
    {
      categories,
      series,
      baseColor = "primary",
      height = 250,
      stacked = false,
      showLegend = true,
      showStackedLabels = false,
      showDataLabels = false,
      isPercentage = false,
      decimalPlaces = 1,
      customStackTotals,
      units,
    },
    ref,
  ) => {
    const finalDecimalPlaces = decimalPlaces !== undefined ? decimalPlaces : 1;
    const chartRef = useRef<HighchartsReact.RefObject>(null);
    const theme = useTheme();
    const colorPalette = COLOR_PALETTES[baseColor];
    const colorArray = [
      colorPalette.L90,
      colorPalette.L80,
      colorPalette.L70,
      colorPalette.L60,
      colorPalette.L50,
      colorPalette.L40,
      colorPalette.L30,
      colorPalette.L20,
      colorPalette.L10,
      colorPalette.main,
      colorPalette.D10,
      colorPalette.D20,
      colorPalette.D30,
    ];

    useImperativeHandle(ref, () => ({
      downloadImage: (filename = "chart") => {
        if (chartRef.current?.chart) {
          addWatermarkToHighchartsExport(chartRef.current.chart, filename);
        }
      },
    }));

    // Si hay una sola serie, asignar colorByPoint y un color distinto a cada barra
    let processedSeries: Highcharts.SeriesColumnOptions[] = [];
    if (series.length === 1) {
      const s = series[0];
      // Asignar un color distinto a cada barra, de oscuro a claro
      const data = s.data.map((value, i) => ({
        y: truncateNumber(value, decimalPlaces),
        color: colorArray[i % colorArray.length],
      }));
      processedSeries = [
        {
          type: "column",
          name: s.name,
          data,
          colorByPoint: true,
          dataLabels: {
            enabled: showDataLabels,
            formatter: function () {
              const value = (this as { y?: number }).y ?? 0;
              const truncated = truncateNumber(value, finalDecimalPlaces);
              const formatted = truncated.toLocaleString("en-US", {
                minimumFractionDigits: finalDecimalPlaces,
                maximumFractionDigits: finalDecimalPlaces,
              });
              return isPercentage ? `${formatted}%` : formatted;
            },
            style: {
              fontFamily: `${theme.typography.caption.fontFamily}`,
              fontSize: `${theme.typography.caption.fontSize}`,
              fontWeight: `${theme.typography.caption.fontWeight}`,
              color: `${theme.palette.text.primary}`,
            },
            y: -5,
          },
        },
      ];
    } else {
      // Si hay varias series, usar el comportamiento normal (un color por serie)
      processedSeries = series.map((s, seriesIndex) => {
        const truncatedData = s.data.map((value) =>
          truncateNumber(value, decimalPlaces),
        );
        return {
          type: "column",
          name: s.name,
          data: truncatedData,
          color: colorArray[seriesIndex % colorArray.length],
          colorByPoint: false,
          dataLabels: {
            enabled: showDataLabels,
            formatter: function () {
              const value = (this as { y?: number }).y ?? 0;
              const truncated = truncateNumber(value, finalDecimalPlaces);
              const formatted = truncated.toLocaleString("en-US", {
                minimumFractionDigits: finalDecimalPlaces,
                maximumFractionDigits: finalDecimalPlaces,
              });
              return isPercentage ? `${formatted}%` : formatted;
            },
            style: {
              fontFamily: `${theme.typography.caption.fontFamily}`,
              fontSize: `${theme.typography.caption.fontSize}`,
              fontWeight: `${theme.typography.caption.fontWeight}`,
              color: `${theme.palette.text.primary}`,
            },
            y: -5,
          },
        };
      });
    }

    const options: Highcharts.Options = {
      chart: {
        type: "column",
        height,
        spacing: [0, 0, 0, 0],
        marginTop: showLegend ? 60 : 10,
      },
      title: {
        text: undefined,
      },
      tooltip: {
        shared: false,
        useHTML: true,
        backgroundColor: "#FFFFFF",
        borderColor: theme.palette.divider,
        borderRadius: 4,
        padding: 6,
        style: {
          color: theme.palette.text.primary,
        },
        formatter: function () {
          const categoryName = this.key;
          const seriesName = this.series.name;
          const value = this.y;
          const seriesColor = this.color;

          const fontSize = theme.typography.overline.fontSize;
          const fontWeight = theme.typography.overline.fontWeight;
          const fontWeightSemiBold = theme.typography.fontWeightSemiBold;
          const letterSpacing = theme.typography.overline.letterSpacing;
          const primaryColor = theme.palette.text.primary;
          const secondaryColor = theme.palette.secondary[400];
          const suffix = units ? ` ${units}` : "";

          return `
            <div>
              <div style="font-size: ${fontSize}; font-weight: ${fontWeightSemiBold}; letter-spacing: ${letterSpacing}; color: ${primaryColor}; margin-bottom: 4px;">
              ${categoryName}
              </div>
              <div style="display: flex; align-items: center;">
              <span style="display: inline-block; width: 8px; height: 8px; background-color: ${seriesColor}; border-radius: 50%; margin-right: 3px;"></span>
              <span style="font-size: ${fontSize}; font-weight: ${fontWeight}; color: ${secondaryColor};">
                ${seriesName}
              </span>
              <span style="font-size: ${fontSize}; font-weight: ${fontWeight}; color: ${primaryColor};">
               &nbsp;- ${value}${suffix}
              </span>
              </div>
            </div>
            `;
        },
      },
      xAxis: {
        categories,
        crosshair: true,
        labels: {
          style: {
            fontFamily: `${theme.typography.caption.fontFamily}`,
            fontSize: `${theme.typography.caption.fontSize}`,
            fontWeight: `${theme.typography.caption.fontWeight}`,
            color: `${theme.palette.text.primary}`,
          },
        },
      },
      yAxis: {
        reversedStacks: false,
        title: {
          text: undefined,
        },
        labels: {
          enabled: false,
        },
        gridLineWidth: 0,
        lineWidth: 0,
        stackLabels: {
          enabled: showStackedLabels && stacked,
          formatter: function () {
            const context = this as { total?: number; x?: number };
            const value =
              customStackTotals && context.x !== undefined
                ? (customStackTotals[context.x] ?? context.total ?? 0)
                : (context.total ?? 0);
            const truncatedValue = truncateNumber(value, finalDecimalPlaces);
            return isPercentage
              ? `${formatNumberWithSeparators(truncatedValue, finalDecimalPlaces)}%`
              : formatNumberWithSeparators(truncatedValue, finalDecimalPlaces);
          },
          style: {
            fontFamily: `${theme.typography.caption.fontFamily}`,
            fontSize: `${theme.typography.caption.fontSize}`,
            fontWeight: `${theme.typography.caption.fontWeight}`,
            color: `${theme.palette.text.primary}`,
          },
          y: -5,
        },
      },
      legend: {
        enabled: showLegend,
        align: "left",
        verticalAlign: "top",
        floating: true,
        y: 0,
        x: 0,
        symbolRadius: 16,
        itemStyle: {
          fontFamily: `${theme.typography.caption.fontFamily}`,
          fontSize: `${theme.typography.caption.fontSize}`,
          fontWeight: `${theme.typography.caption.fontWeight}`,
          color: `${theme.palette.text.primary}`,
        },
      },
      plotOptions: {
        column: {
          stacking: stacked ? "normal" : undefined,
          pointPadding: 0.05,
          groupPadding: 0.1,
          borderRadius: 4,
          borderWidth: 1,
          // colorByPoint: true solo si hay una serie
        },
      },
      series: processedSeries as Highcharts.SeriesOptionsType[],
    };

    useEffect(() => {
      if (chartRef.current?.chart) {
        chartRef.current.chart.reflow();
      }
    }, []);

    return (
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={options}
      />
    );
  },
);

ColumnChartByPoint.displayName = "ColumnChartByPoint";
